import { useEffect, useRef, useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { SingleImageEditor } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { COLOR_CATEGORIES, colorSlug } from "@/lib/colors";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

// Copies this card's colors onto every design already matched with the fabric.
function ApplyToAllButton({ fabric, colors, matchCount, onApply }) {
  const id = `compat-apply-all-${fabric.id}`;
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" data-testid={id} disabled={matchCount < 2}
          className="mt-2 w-full rounded-full border border-kys-blush-border py-1.5 text-[11px] font-medium text-kys-charcoal transition-colors hover:border-kys-accent disabled:cursor-not-allowed disabled:opacity-40">
          Apply to all designs ({matchCount})
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${id}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>Apply colors to every design?</AlertDialogTitle>
          <AlertDialogDescription>
            The colors on all {matchCount} designs matched with <strong>{fabric.name}</strong> will be replaced with: {colors.length ? colors.join(", ") : "no colors"}. Availability, mockups and fabric stock stay the same.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${id}-cancel`}>Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid={`${id}-confirm`} onClick={onApply}>Apply colors</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Multi-select color categories for one design-fabric match.
function ColorPicker({ value, onChange, testid }) {
  const toggle = (c) => onChange(value.includes(c) ? value.filter((x) => x !== c) : [...value, c]);
  return (
    <div data-testid={testid} className="mt-2">
      <p className="text-[10px] font-medium uppercase tracking-wide text-kys-muted">Colors</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {COLOR_CATEGORIES.map((c) => {
          const on = value.includes(c);
          return (
            <button key={c} type="button" data-testid={`${testid}-${colorSlug(c)}`} aria-pressed={on} onClick={() => toggle(c)}
              className={`rounded-full border px-1.5 py-0.5 text-[10px] transition-colors ${on ? "border-kys-accent bg-kys-accent text-white" : "border-kys-blush-border text-kys-charcoal hover:border-kys-accent"}`}>
              {c}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminCompatibility() {
  const [styles, setStyles] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [combos, setCombos] = useState([]);
  const [activeStyle, setActiveStyle] = useState("");

  const loadCombos = () => api.get("/admin/design-fabrics").then((r) => setCombos(r.data));
  useEffect(() => {
    api.get("/admin/styles").then((r) => { setStyles(r.data); if (r.data[0]) setActiveStyle(r.data[0].id); });
    api.get("/admin/fabrics").then((r) => setFabrics(r.data));
    loadCombos();
  }, []);

  const comboFor = (styleId, fabricId) => combos.find((c) => c.style_id === styleId && c.fabric_id === fabricId && !c.archived);

  // Show changes instantly and send saves one at a time, so fast clicks never race or lose colors.
  const queue = useRef(Promise.resolve());
  const pending = useRef(0);
  const setCombo = (fabricId, available, mockup, colors) => {
    const styleId = activeStyle;
    setCombos((cs) => {
      const i = cs.findIndex((c) => c.style_id === styleId && c.fabric_id === fabricId && !c.archived);
      const next = { ...(i >= 0 ? cs[i] : { style_id: styleId, fabric_id: fabricId, colors: [] }), available, mockup_image: mockup || null, ...(colors ? { colors } : {}) };
      return i >= 0 ? cs.map((c, j) => (j === i ? next : c)) : [...cs, next];
    });
    pending.current += 1;
    queue.current = queue.current.then(async () => {
      try {
        await api.post("/admin/design-fabrics", { style_id: styleId, fabric_id: fabricId, available, mockup_image: mockup || null, ...(colors ? { colors } : {}) });
      } catch (e) { toast.error(apiError(e)); }
      pending.current -= 1;
      if (pending.current === 0) await loadCombos();
    });
  };

  const applyToAll = (fabricId, colors) => {
    setCombos((cs) => cs.map((c) => (c.fabric_id === fabricId && !c.archived ? { ...c, colors } : c)));
    pending.current += 1;
    queue.current = queue.current.then(async () => {
      try {
        const r = await api.post("/admin/design-fabrics/apply-colors", { fabric_id: fabricId, colors });
        toast.success(`Colors applied to ${r.data.updated} designs`);
      } catch (e) { toast.error(apiError(e)); }
      pending.current -= 1;
      if (pending.current === 0) await loadCombos();
    });
  };
  const matchCount = (fabricId) => combos.filter((c) => c.fabric_id === fabricId && !c.archived).length;

  const activeFabrics = fabrics.filter((f) => !f.archived);

  return (
    <div>
      <h1 className="font-serif text-3xl text-kys-ink">Design ↔ Fabric Availability</h1>
      <p className="mt-1 max-w-2xl text-sm text-kys-muted">Control exactly which fabrics are available for each design. A fabric available for one design is <strong>not</strong> automatically available for others. Customers only see combinations you mark available.</p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Style picker */}
        <div className="lg:col-span-1">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-kys-muted">Designs</p>
          <div className="no-scrollbar flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
            {styles.filter((s) => !s.archived).map((s) => (
              <button key={s.id} data-testid={`compat-style-${s.name.toLowerCase()}`} onClick={() => setActiveStyle(s.id)}
                className={`whitespace-nowrap rounded px-3 py-2 text-left text-sm font-medium transition ${activeStyle === s.id ? "bg-kys-ink text-white" : "bg-white text-kys-charcoal hover:bg-kys-blush"} border border-kys-blush-border`}>
                {s.name}
              </button>
            ))}
          </div>
        </div>

        {/* Fabric grid for active style */}
        <div className="lg:col-span-3">
          {activeFabrics.length === 0 ? <p className="text-sm text-kys-muted">Add fabrics first.</p> : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
              {activeFabrics.map((f) => {
                const combo = comboFor(activeStyle, f.id);
                const available = !!combo?.available;
                return (
                  <div key={f.id} data-testid={`compat-fabric-${f.id}`} className={`overflow-hidden rounded-lg border bg-white ${available ? "border-kys-accent" : "border-kys-blush-border"}`}>
                    <div className="aspect-square bg-kys-blush">
                      {(combo?.mockup_image || f.images?.[0]) ? <img src={fileUrl(combo?.mockup_image || f.images?.[0])} alt={f.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center px-2 text-center text-[11px] text-kys-muted">{f.name}</div>}
                    </div>
                    <div className="p-2.5">
                      <p className="truncate text-xs font-medium text-kys-ink">{f.name}</p>
                      <button onClick={() => setCombo(f.id, !available, combo?.mockup_image)}
                        className={`mt-2 flex w-full items-center justify-center gap-1 rounded-full py-1.5 text-xs font-semibold transition ${available ? "bg-kys-accent text-white" : "border border-kys-blush-border text-kys-charcoal hover:border-kys-accent"}`}>
                        {available ? <><Check className="h-3.5 w-3.5" /> Available</> : <><X className="h-3.5 w-3.5" /> Unavailable</>}
                      </button>
                      <div className="mt-2 scale-90 origin-left">
                        <SingleImageEditor label="Mockup" value={combo?.mockup_image} onChange={(img) => setCombo(f.id, available, img)} />
                      </div>
                      <ColorPicker value={combo?.colors || []} testid={`compat-colors-${f.id}`}
                        onChange={(cols) => setCombo(f.id, available, combo?.mockup_image, cols)} />
                      {combo && <ApplyToAllButton fabric={f} colors={combo.colors || []} matchCount={matchCount(f.id)}
                        onApply={() => applyToAll(f.id, combo.colors || [])} />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
